/**
 * Jupiter Aggregator Integration
 *
 * Used for trading graduated tokens that have migrated to Raydium/DEXs.
 * Jupiter aggregates liquidity across multiple DEXs for best execution.
 */

import {
  PublicKey,
  VersionedTransaction,
} from '@solana/web3.js';

const JUPITER_QUOTE_API = 'https://quote-api.jup.ag/v6';
const WSOL_MINT = new PublicKey('So11111111111111111111111111111111111111112');

export interface JupiterQuote {
  inputMint: string;
  outputMint: string;
  inAmount: string;
  outAmount: string;
  otherAmountThreshold: string;
  swapMode: string;
  slippageBps: number;
  priceImpactPct: string;
  routePlan: Array<{
    swapInfo: {
      ammKey: string;
      label: string;
      inputMint: string;
      outputMint: string;
      inAmount: string;
      outAmount: string;
      feeAmount: string;
      feeMint: string;
    };
    percent: number;
  }>;
}

export interface JupiterSwapResponse {
  swapTransaction: string;
  lastValidBlockHeight: number;
  prioritizationFeeLamports: number;
}

/**
 * Get a quote from Jupiter for a swap
 */
export async function getQuote(params: {
  inputMint: string;
  outputMint: string;
  amount: string; // in smallest units (lamports for SOL)
  slippageBps: number;
  swapMode?: 'ExactIn' | 'ExactOut';
}): Promise<JupiterQuote> {
  const { inputMint, outputMint, amount, slippageBps, swapMode = 'ExactIn' } = params;

  const url = new URL(`${JUPITER_QUOTE_API}/quote`);
  url.searchParams.set('inputMint', inputMint);
  url.searchParams.set('outputMint', outputMint);
  url.searchParams.set('amount', amount);
  url.searchParams.set('slippageBps', slippageBps.toString());
  url.searchParams.set('swapMode', swapMode);

  const response = await fetch(url.toString());

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Jupiter quote failed: ${error}`);
  }

  return response.json();
}

/**
 * Get swap transaction from Jupiter
 */
export async function getSwapTransaction(params: {
  quote: JupiterQuote;
  userPublicKey: string;
  wrapAndUnwrapSol?: boolean;
  useSharedAccounts?: boolean;
  prioritizationFeeLamports?: number;
}): Promise<JupiterSwapResponse> {
  const {
    quote,
    userPublicKey,
    wrapAndUnwrapSol = true,
    useSharedAccounts = true,
    prioritizationFeeLamports,
  } = params;

  const response = await fetch(`${JUPITER_QUOTE_API}/swap`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      quoteResponse: quote,
      userPublicKey,
      wrapAndUnwrapSol,
      useSharedAccounts,
      prioritizationFeeLamports,
    }),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Jupiter swap failed: ${error}`);
  }

  return response.json();
}

/**
 * Get a buy quote (SOL -> Token)
 */
export async function getBuyQuote(
  tokenMint: string,
  solAmount: bigint,
  slippageBps: number
): Promise<{
  expectedTokens: bigint;
  minTokensOut: bigint;
  priceImpact: number;
  route: JupiterQuote;
}> {
  const quote = await getQuote({
    inputMint: WSOL_MINT.toString(),
    outputMint: tokenMint,
    amount: solAmount.toString(),
    slippageBps,
  });

  return {
    expectedTokens: BigInt(quote.outAmount),
    minTokensOut: BigInt(quote.otherAmountThreshold),
    priceImpact: parseFloat(quote.priceImpactPct),
    route: quote,
  };
}

/**
 * Get a sell quote (Token -> SOL)
 */
export async function getSellQuote(
  tokenMint: string,
  tokenAmount: bigint,
  slippageBps: number
): Promise<{
  expectedSol: bigint;
  minSolOut: bigint;
  priceImpact: number;
  route: JupiterQuote;
}> {
  const quote = await getQuote({
    inputMint: tokenMint,
    outputMint: WSOL_MINT.toString(),
    amount: tokenAmount.toString(),
    slippageBps,
  });

  return {
    expectedSol: BigInt(quote.outAmount),
    minSolOut: BigInt(quote.otherAmountThreshold),
    priceImpact: parseFloat(quote.priceImpactPct),
    route: quote,
  };
}

/**
 * Build swap instructions from Jupiter response
 */
export async function buildSwapInstructions(params: {
  userWallet: PublicKey;
  tokenMint: string;
  action: 'buy' | 'sell';
  amount: bigint;
  slippageBps: number;
  priorityFeeLamports?: number;
}): Promise<{
  transaction: VersionedTransaction;
  expectedOutput: bigint;
  minOutput: bigint;
  priceImpact: number;
}> {
  const { userWallet, tokenMint, action, amount, slippageBps, priorityFeeLamports } = params;

  // Get quote
  let quote: JupiterQuote;
  let expectedOutput: bigint;
  let minOutput: bigint;
  let priceImpact: number;

  if (action === 'buy') {
    const buyQuote = await getBuyQuote(tokenMint, amount, slippageBps);
    quote = buyQuote.route;
    expectedOutput = buyQuote.expectedTokens;
    minOutput = buyQuote.minTokensOut;
    priceImpact = buyQuote.priceImpact;
  } else {
    const sellQuote = await getSellQuote(tokenMint, amount, slippageBps);
    quote = sellQuote.route;
    expectedOutput = sellQuote.expectedSol;
    minOutput = sellQuote.minSolOut;
    priceImpact = sellQuote.priceImpact;
  }

  // Get swap transaction
  const swapResponse = await getSwapTransaction({
    quote,
    userPublicKey: userWallet.toString(),
    prioritizationFeeLamports: priorityFeeLamports,
  });

  // Deserialize the transaction
  const swapTransactionBuf = Buffer.from(swapResponse.swapTransaction, 'base64');
  const transaction = VersionedTransaction.deserialize(swapTransactionBuf);

  return {
    transaction,
    expectedOutput,
    minOutput,
    priceImpact,
  };
}

/**
 * Check if a token has liquidity on Jupiter
 */
export async function hasJupiterLiquidity(tokenMint: string): Promise<boolean> {
  try {
    // Try to get a small quote to see if there's liquidity
    await getQuote({
      inputMint: WSOL_MINT.toString(),
      outputMint: tokenMint,
      amount: '1000000', // 0.001 SOL
      slippageBps: 500,
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * Get token price in SOL via Jupiter
 */
export async function getTokenPriceInSol(tokenMint: string): Promise<number | null> {
  try {
    const quote = await getQuote({
      inputMint: tokenMint,
      outputMint: WSOL_MINT.toString(),
      amount: '1000000', // 1 token (assuming 6 decimals)
      slippageBps: 100,
    });

    // Convert output (lamports) to SOL price per token
    const solOut = Number(quote.outAmount) / 1e9;
    return solOut;
  } catch {
    return null;
  }
}
